import { createFrameReader, createMockFrameReader } from './FrameReader'
import { collectWebGLDiagnostics, createRenderStabilityGuard } from './RenderStabilityGuard'
import type {
  FrameReaderLike,
  FrameReaderOptions,
  FrameReaderSource,
  ManualFrameRequest,
  RenderStabilityGuardLike,
  RenderStabilityGuardOptions,
  RenderStabilityProbe,
  VideoRenderEngineLike,
} from './types'

/** VideoRender 可用性预检依赖：能力快照 + 编码运行时探测结果。 */
export interface VideoRenderAvailabilityInput {
  readonly capabilities?: {
    readonly offscreenTarget?: boolean
    readonly readback?: boolean
  }
  /** 宿主是否提供 WebCodecs VideoEncoder（或等价的 MP4 编码运行时）。 */
  readonly hasVideoEncoder?: boolean
}

export interface VideoRenderAvailability {
  readonly available: boolean
  readonly missing: readonly string[]
}

/**
 * 按「能力组合 + 编码运行时」推导 VideoRender 可用性。
 *
 * 与 `VideoRenderPlugin.graphicsRequirements`（offscreenTarget + readback）互补：
 * requirements 负责 preset 预检门禁，这里负责运行时判定编码运行时是否就绪，
 * 不引入单独的 `videoCapture` 能力字段。
 */
export function resolveVideoRenderAvailability(
  input: VideoRenderAvailabilityInput
): VideoRenderAvailability {
  const missing: string[] = []
  if (input.capabilities?.offscreenTarget !== true) missing.push('offscreenTarget')
  if (input.capabilities?.readback !== true) missing.push('readback')
  if (input.hasVideoEncoder !== true) missing.push('videoEncoder')
  return { available: missing.length === 0, missing: Object.freeze(missing) }
}

export class VideoRenderService {
  readonly #availability: VideoRenderAvailability

  constructor(availability: VideoRenderAvailabilityInput = {}) {
    this.#availability = resolveVideoRenderAvailability(availability)
  }

  /** 当前宿主是否满足 VideoRender 的『能力 + 编码运行时』组合。 */
  get availability(): VideoRenderAvailability {
    return this.#availability
  }

  createStabilityGuard(
    engine: VideoRenderEngineLike | undefined,
    options?: RenderStabilityGuardOptions,
    frameReaderOptions?: FrameReaderOptions
  ): RenderStabilityGuardLike {
    const frameReader = this.createFrameReaderForEngine(engine, {
      ...frameReaderOptions,
      allowBackbufferRead:
        frameReaderOptions?.allowBackbufferRead ?? Boolean(engine?.preserveDrawingBuffer),
    })
    const canReadFrame = Boolean(engine?.renderer || engine?.createRegionCaptureSession)
    const probe: RenderStabilityProbe = {
      getDiagnostics: async () => ({
        ...(await engine?.getGsplatRenderableDiagnostics?.()),
        webgl: collectWebGLDiagnostics(engine?.renderer),
      }),
      waitForAssets: (signal) => engine?.waitForAssets?.(signal),
      flushOrderUpload: (signal) => engine?.flushGsplatOrderUpload?.(signal),
      readFrame: canReadFrame
        ? async (signal) => {
            throwIfAborted(signal)
            const frame = await frameReader.readFrame({
              frameNumber: 0,
              timestampSeconds: 0,
              deltaSeconds: 0,
              width: getReadbackWidth(engine),
              height: getReadbackHeight(engine),
              pixelRatio: 1,
            })
            throwIfAborted(signal)
            return frame
          }
        : undefined,
    }
    return createRenderStabilityGuard(probe, options)
  }

  createFrameReader(source: FrameReaderSource, options?: FrameReaderOptions): FrameReaderLike {
    if (!source.renderer && !source.readPixelsAsync) {
      return createMockFrameReader()
    }
    return createFrameReader(source, options)
  }

  /**
   * 为 engine facade 选择实际可用的 frame source。
   *
   * WebGPU 优先使用 backend-owned capture session；WebGL2 保持原有 renderer/renderTarget
   * 读回路径。返回值始终可直接交给既有 FrameReader 背压与诊断链。
   */
  createFrameReaderForEngine(
    engine: VideoRenderEngineLike | undefined,
    options?: FrameReaderOptions
  ): FrameReaderLike {
    const sessionFactory = engine?.createRegionCaptureSession
    const renderManualFrame = engine?.renderManualFrame ?? engine?.loop?.renderManualFrame
    if (sessionFactory && renderManualFrame) {
      const session = sessionFactory()
      if (session) {
        return createFrameReader(
          {
            readPixelsAsync: async (request, width, height) => {
              const captureRequest = {
                ...request,
                width,
                height,
                pixelRatio: 1,
              }
              const result = await session.capture({
                width,
                height,
                render: async () => {
                  await renderManualFrame(captureRequest)
                },
              })
              return { data: result.pixels, origin: 'top-left' }
            },
            dispose: () => session.dispose(),
          },
          options
        )
      }
    }

    return this.createFrameReader(
      {
        renderer: engine?.renderer,
        renderTarget: engine?.renderTarget,
        preserveDrawingBuffer: engine?.preserveDrawingBuffer,
      },
      options
    )
  }

  async renderManualFrame(
    engine: VideoRenderEngineLike | undefined,
    request: ManualFrameRequest
  ): Promise<void> {
    engine?.stop?.()
    await (engine?.renderManualFrame?.(request) ?? engine?.loop?.renderManualFrame?.(request))
  }
}

export function createVideoRenderService(
  availability: VideoRenderAvailabilityInput = {}
): VideoRenderService {
  return new VideoRenderService(availability)
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException('Video render stabilization was cancelled.', 'AbortError')
  }
}

function getReadbackWidth(engine: VideoRenderEngineLike | undefined): number {
  return (
    engine?.getGraphicsInfo?.().surface.drawingBufferWidth ??
    engine?.renderer?.domElement?.width ??
    1
  )
}

function getReadbackHeight(engine: VideoRenderEngineLike | undefined): number {
  return (
    engine?.getGraphicsInfo?.().surface.drawingBufferHeight ??
    engine?.renderer?.domElement?.height ??
    1
  )
}
